import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  CreateHomeworkPayload,
  GradePayload,
  HomeworkItem,
  HomeworkList,
  HomeworkListStatus,
  HomeworkReview,
  UpdateHomeworkPayload,
} from '@/types/homework';

/**
 * Every call the homework screens make.
 *
 * Its own file rather than more methods on `teacher.api.ts`: homework has two
 * screens and five calls of its own, and a feature's network layer that grows
 * without bound is the one nobody can find anything in (CLAUDE.md §6).
 *
 * No path carries an institute id or an academic year — the server takes both from
 * the caller's token and the institute's current session.
 */
/**
 * Homework travels as multipart because the worksheet travels with it: work
 * half-saved — set, but with its question paper lost — is worse than work that
 * failed outright. The record itself goes in a JSON text field beside the file,
 * the same shape the activity and admission forms use.
 */
function toFormData(
  payload: CreateHomeworkPayload | UpdateHomeworkPayload,
  attachment?: File | null,
): FormData {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  if (attachment) form.append('attachment', attachment);
  return form;
}

/**
 * Hands the Content-Type back to the browser for a multipart request.
 *
 * `apiClient` sets `application/json` for every call, which is right for all but
 * this one: with it in place the boundary is never written and the server reads
 * a multipart body as JSON. Setting it to `undefined` makes axios leave the
 * header off so the browser can write `multipart/form-data; boundary=…` itself.
 */
const MULTIPART = { headers: { 'Content-Type': undefined } };

export const homeworkApi = {
  /**
   * Where one child's uploaded solution is fetched from.
   *
   * A path rather than a call: the file is private (CLAUDE.md §11), so it is
   * downloaded with the access token by `downloadPrivateFile` rather than put
   * in an `<a href>`.
   */
  submissionFilePath: (homeworkUuid: string, studentUuid: string) =>
    `/institute/homework/${homeworkUuid}/submissions/${studentUuid}/attachment`,

  /**
   * Where the worksheet the teacher handed out is fetched from.
   *
   * `worksheet`, not `attachment`: one homework uuid has two files hanging off
   * it going in opposite directions, and the child's own is the one above.
   */
  worksheetPath: (uuid: string) => `/institute/homework/${uuid}/worksheet`,

  /** Everything set for one section, newest due date first — or, asked for, what was withdrawn. */
  list: (classSectionUuid: string, onDate?: string, status?: HomeworkListStatus) =>
    unwrap(
      apiClient.get<ApiSuccess<HomeworkList>>('/institute/homework', {
        params: { classSectionUuid, ...(onDate && { onDate }), ...(status && { status }) },
      }),
    ),

  create: (payload: CreateHomeworkPayload, attachment?: File | null) =>
    unwrap(
      apiClient.post<ApiSuccess<HomeworkItem>>(
        '/institute/homework',
        toFormData(payload, attachment),
        MULTIPART,
      ),
    ),

  /**
   * Corrects work until its due date passes. A worksheet sent here replaces the
   * old one; leaving it out keeps it.
   */
  update: (uuid: string, payload: UpdateHomeworkPayload, attachment?: File | null) =>
    unwrap(
      apiClient.patch<ApiSuccess<HomeworkItem>>(
        `/institute/homework/${uuid}`,
        toFormData(payload, attachment),
        MULTIPART,
      ),
    ),

  /** A soft delete: the marks already given against it are a term's record. */
  remove: (uuid: string) => unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/homework/${uuid}`)),

  /**
   * One piece of work with the *whole roster* against it — not only the
   * children who handed something in, who are the ones a teacher least needs
   * to look for.
   */
  review: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<HomeworkReview>>(`/institute/homework/${uuid}/submissions`)),

  /**
   * Records a mark, feedback, or simply that the work came in.
   *
   * `PUT` on the pair: marking a child twice corrects the first mark rather
   * than adding a second. Returns the whole review, so the screen never has to
   * guess what the summary counts became.
   */
  grade: (uuid: string, studentUuid: string, payload: GradePayload) =>
    unwrap(
      apiClient.put<ApiSuccess<HomeworkReview>>(
        `/institute/homework/${uuid}/submissions/${studentUuid}`,
        payload,
      ),
    ),
};
